import type { NavigateFunction } from "react-router-dom";

/**
 * The app lives at cards.courtshuffle.com, and courtshuffle.com keeps only
 * the landing page. One build serves both, so each page checks where it is;
 * development serves everything from one address.
 */
export const APP_ORIGIN = "https://cards.courtshuffle.com";
const LANDING_ORIGIN = "https://courtshuffle.com";
const LANDING_HOSTS = new Set(["courtshuffle.com", "www.courtshuffle.com"]);

type Place = Pick<Location, "hostname" | "pathname" | "search" | "hash">;

const isLandingSite = (place: Place = window.location) =>
  LANDING_HOSTS.has(place.hostname);

/**
 * Where this page belongs instead, or null when it's already there: app
 * pages, old share links included, move to the app's domain, and www moves
 * to courtshuffle.com. Checked before the app starts, since an installed
 * copy opens pages without asking the server.
 */
export const relocation = (place: Place = window.location): string | null => {
  if (!isLandingSite(place)) return null;
  const rest = place.pathname + place.search + place.hash;
  if (place.pathname !== "/") return APP_ORIGIN + rest;
  return place.hostname === "courtshuffle.com" ? null : LANDING_ORIGIN + rest;
};

/** Whether this is the app's own domain, where the bare address opens the
 * game instead of the landing page. */
export const isAppSite = (origin: string = window.location.origin) =>
  origin === APP_ORIGIN;

/** Opens `path` in the app: the app's domain from the landing page,
 * otherwise a route change. */
export const goToApp = (navigate: NavigateFunction, path: string) => {
  if (isLandingSite()) window.location.assign(APP_ORIGIN + path);
  else navigate(path);
};
